import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { MdEmail, MdFamilyRestroom, MdPersonAddAlt1, MdOutlinePendingActions, MdRefresh, MdDeleteOutline, MdClose, MdEdit } from "react-icons/md";
import { BsSearch } from "react-icons/bs";
import { format, isValid } from "date-fns";
import Cookies from "js-cookie";
import baseURL from "../../config/api";
import useGroup from "../../hook/useGroup";
import useAuth from "../../hook/useAuth";
import useDelete from "../../hook/useDelete";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];

type InvitationStatusPresentation = {
    label: string;
    className: string;
    dotClassName: string;
};

const getInvitationStatusPresentation = (status?: string): InvitationStatusPresentation => {
    const normalizedStatus = (status ?? "").trim().toLowerCase();

    switch (normalizedStatus) {
        case "pending":
            return {
                label: "Pending",
                className: "border-yellow-200 bg-yellow-50 text-yellow-700",
                dotClassName: "bg-yellow-500"
            };

        case "accepted":
            return {
                label: "Accepted",
                className: "border-green-200 bg-green-50 text-green-700",
                dotClassName: "bg-green-500"
            };

        case "revoked":
        case "cancelled":
        case "canceled":
            return {
                label: "Revoked",
                className: "border-gray-200 bg-gray-100 text-gray-600",
                dotClassName: "bg-gray-500"
            };

        case "declined":
        case "rejected":
            return {
                label: "Declined",
                className: "border-red-200 bg-red-50 text-red-700",
                dotClassName: "bg-red-500"
            };

        default:
            return {
                label: status?.trim() || "Unknown",
                className: "border-gray-200 bg-gray-50 text-gray-600",
                dotClassName: "bg-gray-400"
            };
    }
};

const InviteMembers = () => {
    const token = Cookies.get("token-my-cdir");
    const { auth }: any = useAuth();
    const { selectedGroupId }: any = useGroup();
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();

    const [invitations, setInvitations] = useState<any[]>([]);
    const [invitationCount, setInvitationCount] = useState<number>(0);
    const [pendingInvitationCount, setPendingInvitationCount] = useState<number>(0);
    const [invitationPage, setInvitationPage] = useState<number>(0);
    const [invitationSize, setInvitationSize] = useState<number>(10);

    const [showInvitePopup, setShowInvitePopup] = useState<boolean>(false);
    const [editingInvitationId, setEditingInvitationId] = useState<number | null>(null);
    const [inviteEmail, setInviteEmail] = useState<string>("");
    const [invitationSearch, setInvitationSearch] = useState<string>("");

    const [isInvitationLoading, setIsInvitationLoading] = useState<boolean>(false);
    const [isInviteSubmitting, setIsInviteSubmitting] = useState<boolean>(false);
    const [invitationActionId, setInvitationActionId] = useState<number | null>(null);

    const [invitationMessage, setInvitationMessage] = useState<string>("");
    const [invitationError, setInvitationError] = useState<string>("");

    const getHeaders = (includeJson = false): HeadersInit => ({
        Authorization: `Bearer ${token ?? ""}`,
        ...(includeJson ? { "Content-Type": "application/json" } : {}),
    });

    const readResponse = async (response: Response): Promise<any> => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.message || "The request could not be completed.");
        }
        return data;
    }

    const fetchInvitations = async (
        page = invitationPage,
        size = invitationSize,
        search = invitationSearch
    ) => {
        if (!selectedGroupId || selectedGroupId <= 0) {
            setInvitations([]);
            setInvitationCount(0);
            setIsInvitationLoading(false);
            return;
        }

        try {
            setIsInvitationLoading(true);

            const trimmedSearch = search?.trim();
            const url = trimmedSearch
                ? `${baseURL}memberInvitation/GetMemberInvitationsPagingByGroupIdAndSearchByEmail/${encodeURIComponent(trimmedSearch)},${selectedGroupId},${page},${size}`
                : `${baseURL}memberInvitation/GetMemberInvitationsPagingByGroupId/${selectedGroupId},${page},${size}`;

            const response = await fetch(url, { headers: getHeaders() });
            const data = await readResponse(response);

            setInvitations(data?.memberInvitations ?? []);
            setInvitationCount(data?.count ?? 0);
            setPendingInvitationCount(data?.pendingCount ?? 0);
        } catch (error) {
            console.error("Failed to load invitations:", error);
            setInvitations([]);
            setInvitationCount(0);
            setInvitationError(
                error instanceof Error ? error.message : "Unable to load invitations."
            );
        } finally {
            setIsInvitationLoading(false);
        }
    }

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            fetchInvitations(invitationPage, invitationSize, invitationSearch);
        }, invitationSearch.trim() ? 350 : 0);

        return () => window.clearTimeout(timeoutId);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId, invitationPage, invitationSize, invitationSearch]);

    const findExistingPendingInvitation = async (email: string): Promise<any | null> => {
        const normalizedEmail = email?.trim()?.toLowerCase();

        if (!normalizedEmail) return null;

        const url = `${baseURL}memberInvitation/GetPendingInvitation/${selectedGroupId},${encodeURIComponent(normalizedEmail)}`;

        const response = await fetch(url, { headers: getHeaders() });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return null;
        }

        const existingEmail = data?.email?.trim()?.toLowerCase() ?? "";

        return existingEmail === normalizedEmail ? data : null;
    }

    const handleEditInvitation = (invitation: any) => {
        if (!invitation?.id || String(invitation.status ?? "").trim().toLowerCase() !== "pending") return;

        setEditingInvitationId(Number(invitation.id));
        setInviteEmail(String(invitation.email ?? ""));
        setInvitationError("");
        setInvitationMessage("");
        setShowInvitePopup(true);
    }

    const handleSendInvitation = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const email = inviteEmail.trim().toLowerCase();

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setInvitationError("Please enter a valid email address.");
            return;
        }

        if (!selectedGroupId || selectedGroupId <= 0) {
            setInvitationError("Please select a church first.");
            return;
                }

        if (!auth?.id) {
            setInvitationError("Unable to identify the current user. Please sign in again.");
            return;

        }

        try {
            setIsInviteSubmitting(true);
            setInvitationError("");
            setInvitationMessage("");

            if (editingInvitationId !== null) {
                const updateResponse = await fetch(
                    `${baseURL}memberInvitation/UpdateInvitation?userId=${encodeURIComponent(auth.id)}`,
                    {
                        method: "PUT",
                        headers: getHeaders(true),
                        body: JSON.stringify({ 
                            id: editingInvitationId,
                            email,
                             groupId: selectedGroupId 
                        })
                    }
                );

                const data = await readResponse(updateResponse);
                setInvitationMessage(data.message || "Invitation updated successfully.");
                setInviteEmail("");
                setEditingInvitationId(null);
                setShowInvitePopup(false);
                await fetchInvitations(invitationPage, invitationSize, invitationSearch);
                return;
            }

            const existingInvitation = await findExistingPendingInvitation(email);

            const existingId = existingInvitation?.id;

            if (existingId) {
                const resendResponse = await fetch(
                    `${baseURL}memberInvitation/${existingId}/Resend?userId=${encodeURIComponent(auth.id)}`,
                    {
                        method: "POST",
                        headers: getHeaders()
                    }
                );

                const data = await readResponse(resendResponse);

                setInvitationMessage(
                    data.message ||
                    `An invitation was already pending for ${email}. A fresh invitation email has been sent.`
                );
            } else {
                const createResponse = await fetch(
                    `${baseURL}memberInvitation/CreateMemberInvitation?userId=${encodeURIComponent(auth.id)}`,
                    {
                        method: "POST",
                        headers: getHeaders(true),
                        body: JSON.stringify({ email, groupId: selectedGroupId })
                    }
                );

                const data = await readResponse(createResponse);

                setInvitationMessage(data.message || "Invitation sent successfully.");
            }

            setInviteEmail("");
            setEditingInvitationId(null);
            setShowInvitePopup(false);
            setInvitationPage(0);
            await fetchInvitations(0, invitationSize, invitationSearch);
        } catch (error) {
            setInvitationError(
                error instanceof Error ? error.message : "Unable to send invitation."
            );

        } finally {
            setIsInviteSubmitting(false);

        }
    }

    const handleResendInvitation = async (id: number) => {
        if (!auth?.id) {
            setInvitationError("Unable to identify the current user. Please sign in again.");
            return;
        }

        try {
            setInvitationActionId(id);
            setInvitationError("");
            setInvitationMessage("");

            const response = await fetch(
                `${baseURL}memberInvitation/${id}/Resend?userId=${encodeURIComponent(auth.id)}`,
                {
                    method: "POST",
                    headers: getHeaders()
                }
            );

            const data = await readResponse(response);
            setInvitationMessage(
                data.message || "Invitation resent successfully."
            );
            await fetchInvitations();
        } catch (error) {
            setInvitationError(
                error instanceof Error ? error.message : "Unable to resend invitation."
            );
        } finally {
            setInvitationActionId(null);
        }
    }

    const handleRemoveInvitation = async (id: number) => {
        setDeleteData({
            id: id,
            tilte: `Delete`,
            summary: `Remove this invitation? The invitation link will no longer work.?`,
            isDelete: (callback: any) => {
                deleteTheInvitation(id, callback);
            }
        });
        setDeletePopup(true);
    }

    const deleteTheInvitation = async (id: number, callback: any) => {
        if (!auth?.id) {
            setInvitationError("Unable to identify the current user. Please sign in again.");
            return;
        }

        try {
            setInvitationActionId(id);
            setInvitationError("");
            setInvitationMessage("");

            const response = await fetch(
                `${baseURL}memberInvitation/${id}?userId=${encodeURIComponent(auth.id)}`,
                {
                    method: "DELETE",
                    headers: getHeaders()
                }
            );

            const data = await readResponse(response);
            setInvitationMessage(
                data.message || "Invitation removed successfully."
            );

            const nextPage =
                invitations.length === 1 && invitationPage > 0
                    ? invitationPage - 1
                    : invitationPage;

            setInvitationPage(nextPage);
            await fetchInvitations(nextPage);

            callback();
        } catch (error) {
            setInvitationError(
                error instanceof Error ? error.message : "Unable to remove invitation."
            );
        } finally {
            setInvitationActionId(null);
        }
    }

    const handlePageSizeChange = (value: number) => {
        setInvitationSize(value);
        setInvitationPage(0);
    }

    const totalPages = Math.ceil(invitationCount / invitationSize);
    const firstRecord = invitationCount === 0 ? 0 : invitationPage * invitationSize + 1;
    const lastRecord = Math.min((invitationPage + 1) * invitationSize, invitationCount);

    return (
        <section className="user__container p-3 xm:p-4 overflow-x-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                    <MdFamilyRestroom className="text-lg md:text-xl" />
                    <h1 className="text-base md:text-lg font-medium">Members</h1>
                </div>

                <button
                    type="button"
                    onClick={() => {
                        setInviteEmail("");
                        setInvitationError("");
                        setInvitationMessage("");
                        setShowInvitePopup(true);
                    }}
                    disabled={!selectedGroupId || selectedGroupId <= 0}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-secondary px-4 py-2 text-sm font-medium text-white hover:bg-custom-blue disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <MdPersonAddAlt1 size={18} />
                    Invite Member
                </button>
            </div>

            <div className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-blue-50 p-2 text-secondary">
                            <MdOutlinePendingActions size={23} />
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-gray-800">
                                Pending Invitations
                            </h2>
                            <p className="text-xs text-gray-500">
                                Manage invitations sent to prospective members.
                            </p>
                        </div>
                        <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-secondary">
                            {pendingInvitationCount}
                        </span>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row">
                        <div className="flex items-center rounded-md border px-3 py-2">
                            <BsSearch className="text-gray-400" />
                            <input
                                type="search"
                                value={invitationSearch}
                                placeholder="Search email..."
                                onChange={(event) => {
                                    setInvitationSearch(event.target.value);
                                    setInvitationPage(0);
                                    setInvitationError("");
                                }}
                                className="ml-2 w-full text-sm outline-none sm:w-48"
                            />
                        </div>

                        <button
                            type="button"
                            onClick={() => fetchInvitations()}
                            disabled={isInvitationLoading}
                            className="flex items-center justify-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                        >
                            <MdRefresh size={18} />
                            Refresh
                        </button>
                    </div>
                </div>

                {
                    (invitationMessage || invitationError) && (
                        <div className="space-y-2 px-4 pt-3">
                            {
                                invitationMessage && (
                                    <div className="flex items-start justify-between gap-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                                        <span>{invitationMessage}</span>

                                        <button
                                            type="button"
                                            onClick={() => setInvitationMessage("")}
                                            className="shrink-0 rounded p-0.5 text-green-600 hover:bg-green-100 hover:text-green-800"
                                            aria-label="Dismiss success message"
                                        >
                                            <MdClose size={18} />
                                        </button>
                                    </div>
                                )
                            }

                            {
                                invitationError && (
                                    <div className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                        <span>{invitationError}</span>

                                        <button
                                            type="button"
                                            onClick={() => setInvitationError("")}
                                            className="shrink-0 rounded p-0.5 text-red-600 hover:bg-red-100 hover:text-red-800"
                                            aria-label="Dismiss error message"
                                        >
                                            <MdClose size={18} />
                                        </button>
                                    </div>
                                )
                            }
                        </div>
                    )
                }

                <div className="p-4">
                    {
                        isInvitationLoading
                            ? (
                                <div className="flex justify-center py-10">
                                    <div className="h-7 w-7 animate-spin rounded-full border-4 border-secondary border-r-transparent" />
                                </div>
                            ) : invitations.length > 0
                                ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full min-w-[650px]">
                                            <thead>
                                                <tr className="border-b text-left text-xs uppercase text-gray-500">
                                                    <th className="px-3 py-3">Email Address</th>
                                                    <th className="px-3 py-3">Invited Date</th>
                                                    <th className="px-3 py-3">Status</th>
                                                    <th className="px-3 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    invitations.map((invitation) => {
                                                        const id = invitation.id;
                                                        const email = invitation.email ?? "";
                                                        const status = invitation.status ?? "";
                                                        const statusPresentation = getInvitationStatusPresentation(status);
                                                        const normalizedStatus = String(status).trim().toLowerCase();
                                                        const canManagePendingInvitation = normalizedStatus === "pending";
                                                        const invitedAt = invitation.invitedAt ?? "";
                                                        const parsedDate = invitedAt ? new Date(invitedAt) : null;

                                                        return (
                                                            <tr
                                                                key={id}
                                                                className="border-b last:border-0 hover:bg-gray-50"
                                                            >
                                                                <td className="px-3 py-4">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="rounded-full bg-gray-100 p-2 text-gray-500">
                                                                            <MdEmail size={18} />
                                                                        </div>
                                                                        <span className="break-all text-sm font-medium text-gray-800">
                                                                            {email}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-3 py-4 text-sm text-gray-600">
                                                                    {
                                                                        parsedDate && isValid(parsedDate)
                                                                            ? format(parsedDate, "MMM dd, yyyy")
                                                                            : "-"
                                                                    }
                                                                </td>
                                                                <td className="px-3 py-4">
                                                                    <span
                                                                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${statusPresentation.className}`}
                                                                    >
                                                                        <span className={`h-1.5 w-1.5 rounded-full ${statusPresentation.dotClassName}`} />
                                                                        {statusPresentation.label}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-4">
                                                                    <div className="flex items-center justify-end gap-2">
                                                                        {canManagePendingInvitation && (
                                                                            <button
                                                                                type="button"
                                                                                disabled={invitationActionId === id}
                                                                                onClick={() => handleEditInvitation(invitation)}
                                                                                className="flex items-center gap-1 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                                                                title="Edit pending invitation"
                                                                            >
                                                                                <MdEdit size={15} />
                                                                                Edit
                                                                            </button>
                                                                        )}

                                                                        <button
                                                                            type="button"
                                                                            disabled={invitationActionId === id || !canManagePendingInvitation}
                                                                            onClick={() => id && handleResendInvitation(id)}
                                                                            className="flex items-center gap-1 rounded-md border border-blue-200 px-3 py-2 text-xs font-medium text-secondary hover:bg-blue-50 disabled:opacity-50"
                                                                        >
                                                                            <MdRefresh size={16} />
                                                                            Resend
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            disabled={invitationActionId === id}
                                                                            onClick={() => id && handleRemoveInvitation(id)}
                                                                            className="flex items-center gap-1 rounded-md border border-red-200 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                                                                        >
                                                                            <MdDeleteOutline size={16} />
                                                                            Remove
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="py-10 text-center">
                                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                                            <MdOutlinePendingActions size={28} />
                                        </div>
                                        <h3 className="mt-3 text-sm font-semibold text-gray-700">
                                            No Invitations
                                        </h3>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Invitations you send will appear here.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setInviteEmail("");
                                                setInvitationError("");
                                                setShowInvitePopup(true);
                                            }}
                                            disabled={!selectedGroupId || selectedGroupId <= 0}
                                            className="mt-4 text-sm font-semibold text-secondary hover:underline disabled:opacity-50"
                                        >
                                            Invite your first member
                                        </button>
                                    </div>
                                )
                    }
                </div>

                <div className="flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-2 text-xs text-gray-500 sm:flex-row sm:items-center sm:gap-4">
                        <span>
                            Showing {firstRecord} - {lastRecord} of {invitationCount} invitations
                        </span>
                        <label className="flex items-center gap-2">
                            Rows per page
                            <select
                                value={invitationSize}
                                onChange={(event) => handlePageSizeChange(Number(event.target.value))}
                                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 outline-none focus:border-secondary"
                            >
                                {
                                    PAGE_SIZE_OPTIONS.map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))
                                }
                            </select>
                        </label>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            disabled={invitationPage === 0 || isInvitationLoading}
                            onClick={() => setInvitationPage((page) => Math.max(0, page - 1))}
                            className="rounded border px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <span className="px-2 text-xs text-gray-600">
                            Page {invitationCount === 0 ? 0 : invitationPage + 1} of {totalPages}
                        </span>
                        <button
                            type="button"
                            disabled={
                                invitationPage + 1 >= totalPages ||
                                isInvitationLoading
                            }
                            onClick={() => setInvitationPage((page) => page + 1)}
                            className="rounded border px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {
                showInvitePopup && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
                        <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl">
                            <div className="flex items-center justify-between border-b p-5">
                                <div className="flex items-center gap-3">
                                    <div className="rounded-lg bg-blue-50 p-2 text-secondary">
                                        {editingInvitationId !== null ? <MdEdit size={23} /> : <MdPersonAddAlt1 size={23} />}
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">
                                            {editingInvitationId !== null ? "Edit Invitation" : "Invite Member"}
                                        </h2>
                                        <p className="text-xs text-gray-500">
                                            {editingInvitationId !== null ? "Update the email address for this pending invitation." : "Send an invitation to join your church."}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowInvitePopup(false);
                                        setEditingInvitationId(null);
                                        setInviteEmail("");
                                        setInvitationError("");
                                    }}
                                    disabled={isInviteSubmitting}
                                    className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
                                    aria-label="Close invitation dialog"
                                >
                                    <MdClose size={23} />
                                </button>
                            </div>

                            <form onSubmit={handleSendInvitation}>
                                <div className="p-5">
                                    <label className="mb-2 block text-sm font-medium text-gray-700">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex items-center rounded-md border border-gray-300 px-3 focus-within:border-secondary">
                                        <MdEmail className="text-gray-400" size={19} />
                                        <input
                                            type="email"
                                            value={inviteEmail}
                                            onChange={(event) => {
                                                setInviteEmail(event.target.value);
                                                setInvitationError("");
                                            }}
                                            placeholder="Enter member email address"
                                            className="w-full px-3 py-3 text-sm outline-none"
                                            required
                                            autoFocus
                                        />
                                    </div>

                                    {
                                        invitationError && (
                                            <p className="mt-2 text-xs text-red-600">{invitationError}</p>
                                        )
                                    }

                                    <div className="mt-4 rounded-md border border-blue-100 bg-blue-50 p-3">
                                        <p className="text-xs leading-5 text-blue-800">
                                            If this email already has a pending invitation, a fresh
                                            invitation link will be sent instead of creating a duplicate.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 border-t bg-gray-50 p-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowInvitePopup(false);
                                            setEditingInvitationId(null);
                                            setInviteEmail("");
                                            setInvitationError("");
                                        }}
                                        disabled={isInviteSubmitting}
                                        className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={isInviteSubmitting}
                                        className="flex items-center gap-2 rounded-md bg-secondary px-5 py-2 text-sm text-white hover:bg-custom-blue disabled:opacity-60"
                                    >
                                        {
                                            isInviteSubmitting
                                                ? (
                                                    <>
                                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
                                                        {editingInvitationId !== null ? "Saving..." : "Sending..."}
                                                    </>
                                                ) : (
                                                    <>
                                                        {editingInvitationId !== null ? <MdEdit size={17} /> : <MdPersonAddAlt1 size={17} />}

                                                        {editingInvitationId !== null ? "Save Changes" : "Send Invitation"}
                                                    </>
                                                )
                                        }
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
        </section>
    );
};

export default InviteMembers;
